import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Share2,
  Palette,
  BarChart2,
  Sparkles,
  Home,
  Loader2,
  Globe,
  X,
  ArrowRightLeft,
  Link as LinkIcon,
  CheckCircle2,
  Save,
  Upload,
  RefreshCw,
  Phone,
  Mail,
  User,
  DollarSign,
  Eye,
  TrendingUp,
  TrendingDown,
  MessageSquare,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTeamAgents } from "@/hooks/useTeamAgents";
import { ZillowIcon } from "@/components/icons/ZillowIcon";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useCMAQuery, useCMACompsQuery, useCMAUpdateMutation } from "@/hooks/useCMAData";
import { CMACompsManager } from "@/components/CMACompsManager";
import { CMAPricingDisplay } from "@/components/CMAPricingDisplay";
import { CMAAnalysis } from "@/components/CMAAnalysis";
import { CMAMap } from "@/components/CMAMap";
import { CMADocuments } from "@/components/CMADocuments";
import { cn } from "@/lib/utils";
import { getContrastColor } from "@/lib/colorUtils";

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const VALID_SECTIONS = ["overview", "comps", "analysis", "design", "share"] as const;
type Section = (typeof VALID_SECTIONS)[number];

const NAV_ITEMS: { id: Section; label: string; icon: React.ReactNode }[] = [
  { id: "overview", label: "Overview", icon: <Home className="h-4 w-4" /> },
  { id: "comps", label: "Comps", icon: <BarChart2 className="h-4 w-4" /> },
  { id: "analysis", label: "Analysis", icon: <Sparkles className="h-4 w-4" /> },
  { id: "design", label: "Design", icon: <Palette className="h-4 w-4" /> },
  { id: "share", label: "Share", icon: <Share2 className="h-4 w-4" /> },
];

export default function CMAReport() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeSection, setActiveSection] = useState<Section>("overview");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Design state (local edits before save)
  const [designEdits, setDesignEdits] = useState<{
    custom_agent_name?: string;
    custom_agent_headshot_url?: string;
    custom_agent_phone?: string;
    custom_agent_email?: string;
    custom_logo_url?: string;
    custom_footer_logo_url?: string;
    primary_brand_color?: string;
    secondary_brand_color?: string;
    agent_notes?: string;
    seller_name?: string;
    custom_property_image_url?: string;
    // Net sheet
    net_sheet_mortgage_payoff?: string;
    net_sheet_commission_pct?: string;
    net_sheet_closing_cost_pct?: string;
    net_sheet_seller_credits?: string;
    net_sheet_other_fees?: string;
  }>({});
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [agentPriceLow, setAgentPriceLow] = useState<string>("");
  const [agentPriceHigh, setAgentPriceHigh] = useState<string>("");
  const [isSavingAgentPrice, setIsSavingAgentPrice] = useState(false);
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingFooterLogo, setIsUploadingFooterLogo] = useState(false);
  const [isUploadingHeadshot, setIsUploadingHeadshot] = useState(false);
  const [isUploadingPropertyImage, setIsUploadingPropertyImage] = useState(false);
  const [isFetchingZestimate, setIsFetchingZestimate] = useState(false);
  const [isRefreshingProperty, setIsRefreshingProperty] = useState(false);
  const [isRefreshingMarketData, setIsRefreshingMarketData] = useState(false);

  const { data: cma, isLoading } = useCMAQuery(id);
  const { data: comps = [] } = useCMACompsQuery(id);
  const updateCMA = useCMAUpdateMutation(id!);
  const { agents } = useTeamAgents();

  const hoverColor = useMemo(() => {
    const base = cma?.secondary_brand_color || cma?.primary_brand_color || "#1b489b";
    const hex = base.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const wa = (255 * 50) / 100;
    const nr = Math.min(255, Math.round(r + wa));
    const ng = Math.min(255, Math.round(g + wa));
    const nb = Math.min(255, Math.round(b + wa));
    return `#${nr.toString(16).padStart(2, "0")}${ng.toString(16).padStart(2, "0")}${nb.toString(16).padStart(2, "0")}`;
  }, [cma?.secondary_brand_color, cma?.primary_brand_color]);

  // URL-sync for section tabs
  useEffect(() => {
    const section = searchParams.get("section") as Section | null;
    if (section && VALID_SECTIONS.includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  useEffect(() => {
    if (cma) {
      setAgentPriceLow(cma.agent_price_low ? String(cma.agent_price_low) : "");
      setAgentPriceHigh(cma.agent_price_high ? String(cma.agent_price_high) : "");
      setDesignEdits({
        custom_agent_name: cma.custom_agent_name || "",
        custom_agent_headshot_url: cma.custom_agent_headshot_url || "",
        custom_agent_phone: cma.custom_agent_phone ? formatPhone(cma.custom_agent_phone) : "",
        custom_agent_email: cma.custom_agent_email || "",
        custom_logo_url: cma.custom_logo_url || "",
        custom_footer_logo_url: cma.custom_footer_logo_url || "",
        primary_brand_color: cma.primary_brand_color || "#000000",
        secondary_brand_color: cma.secondary_brand_color || "#666666",
        agent_notes: cma.agent_notes || "",
        seller_name: cma.seller_name || "",
        custom_property_image_url: cma.custom_property_image_url || "",
        net_sheet_mortgage_payoff: cma.net_sheet_mortgage_payoff != null ? String(cma.net_sheet_mortgage_payoff) : "",
        net_sheet_commission_pct: cma.net_sheet_commission_pct != null ? String(cma.net_sheet_commission_pct) : "",
        net_sheet_closing_cost_pct: cma.net_sheet_closing_cost_pct != null ? String(cma.net_sheet_closing_cost_pct) : "",
        net_sheet_seller_credits: cma.net_sheet_seller_credits != null ? String(cma.net_sheet_seller_credits) : "",
        net_sheet_other_fees: cma.net_sheet_other_fees != null ? String(cma.net_sheet_other_fees) : "",
      });
    }
  }, [cma?.id]);

  // Populate fields when an agent is selected from the roster
  useEffect(() => {
    if (!selectedAgentId || selectedAgentId === "none") return;
    const agent = agents.find((a) => a.id === selectedAgentId);
    if (!agent) return;
    setDesignEdits((prev) => ({
      ...prev,
      custom_agent_name: agent.name,
      custom_agent_headshot_url: agent.headshot_url || prev.custom_agent_headshot_url,
      custom_agent_email: agent.user_email || prev.custom_agent_email,
    }));
  }, [selectedAgentId, agents]);

  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (cma && cma.price === null && !autoFetchedRef.current) {
      autoFetchedRef.current = true;
      fetchZestimate(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cma?.id, cma?.price]);

  const handleSectionChange = useCallback(
    (section: Section) => {
      setActiveSection(section);
      setSearchParams({ section }, { replace: true });
    },
    [setSearchParams]
  );

  const handleRefreshMarketData = async () => {
    if (!cma) return;
    const zip = cma.zip || cma.property_data?.address?.zipcode;
    if (!zip) {
      toast({ title: "No zip code found on this CMA", variant: "destructive" });
      return;
    }
    setIsRefreshingMarketData(true);
    try {
      // Use fetch directly to bypass the 10s global client timeout —
      // fetch-neighborhood-data makes 4 Zillow calls and needs ~30-60s
      const { data: { session } } = await supabase.auth.getSession();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90_000);

      let data: any;
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-neighborhood-data`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session?.access_token}`,
              "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({ zip }),
            signal: controller.signal,
          }
        );
        data = await res.json();
      } finally {
        clearTimeout(timeoutId);
      }

      if (data?.error) throw new Error(data.error);

      const neighborhoodData = data?.data;
      const soldDomMedian: number | null = neighborhoodData?.metrics?.soldDomMedian ?? null;
      if (soldDomMedian == null) throw new Error("No DOM data returned for this zip code");

      // Save to CMA
      const existingPropertyData = cma.property_data || {};
      await supabase
        .from("cmas")
        .update({
          property_data: { ...existingPropertyData, zip_sold_dom_median: soldDomMedian },
          zip_sold_dom_median: soldDomMedian,
          neighborhood_data: neighborhoodData,
          neighborhood_data_updated_at: new Date().toISOString(),
        })
        .eq("id", cma.id);

      const demandRating = neighborhoodData?.analysis?.demandRating;
      const buyerTrend = neighborhoodData?.analysis?.buyerTrend;
      const description = demandRating != null && buyerTrend
        ? `Demand: ${demandRating}/10 · ${buyerTrend} market`
        : `Median days on market: ${soldDomMedian} days`;
      toast({ title: "Market data refreshed", description });
      await queryClient.refetchQueries({ queryKey: ["cma", cma.id] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to refresh market data";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsRefreshingMarketData(false);
    }
  };

  const handlePublishToggle = async () => {
    if (!cma) return;
    setIsPublishing(true);
    try {
      await updateCMA.mutateAsync({
        is_published: !cma.is_published,
        published_at: !cma.is_published ? new Date().toISOString() : null,
      });
      toast({ title: cma.is_published ? "CMA Unpublished" : "CMA Published" });
    } catch {
      toast({ title: "Error", description: "Failed to update publish status", variant: "destructive" });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!cma?.is_published) {
      toast({ title: "Not published", description: "Publish the CMA first", variant: "destructive" });
      return;
    }
    const url = `${window.location.origin}/cma/public/${cma.id}`;
    await navigator.clipboard.writeText(url).catch(() => {});
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
    toast({ title: "Link copied!" });
  };

  const handleConvert = async () => {
    if (!cma) return;
    if (cma.converted_north_id) {
      navigate(`/seller-report/${cma.converted_north_id}`);
      return;
    }
    setIsConverting(true);
    try {
      const { data, error } = await supabase.functions.invoke("convert-cma-to-north", {
        body: { cmaId: cma.id },
      });
      if (error) throw error;
      toast({ title: "Converted!", description: "CMA activated as North Report." });
      queryClient.invalidateQueries({ queryKey: ["cma", cma.id] });
      navigate(`/seller-report/${data.northId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Conversion failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsConverting(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingLogo(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/cma-logo-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from("logos")
        .upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from("logos").getPublicUrl(filePath);
      setDesignEdits((prev) => ({ ...prev, custom_logo_url: publicUrl }));
    } catch {
      toast({ title: "Upload failed", description: "Could not upload logo", variant: "destructive" });
    } finally {
      setIsUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleFooterLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingFooterLogo(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/cma-footer-logo-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from("logos")
        .upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from("logos").getPublicUrl(filePath);
      setDesignEdits((prev) => ({ ...prev, custom_footer_logo_url: publicUrl }));
    } catch {
      toast({ title: "Upload failed", description: "Could not upload footer logo", variant: "destructive" });
    } finally {
      setIsUploadingFooterLogo(false);
      e.target.value = "";
    }
  };

  const handleHeadshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingHeadshot(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/cma-headshot-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from("logos").upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from("logos").getPublicUrl(filePath);
      setDesignEdits((prev) => ({ ...prev, custom_agent_headshot_url: publicUrl }));
    } catch {
      toast({ title: "Upload failed", description: "Could not upload headshot", variant: "destructive" });
    } finally {
      setIsUploadingHeadshot(false);
      e.target.value = "";
    }
  };

  const handlePropertyImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingPropertyImage(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/cma-property-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from("logos").upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from("logos").getPublicUrl(filePath);
      setDesignEdits((prev) => ({ ...prev, custom_property_image_url: publicUrl }));
      await updateCMA.mutateAsync({ custom_property_image_url: publicUrl });
      queryClient.invalidateQueries({ queryKey: ["cma", id] });
      toast({ title: "Property photo updated" });
    } catch {
      toast({ title: "Upload failed", description: "Could not upload photo", variant: "destructive" });
    } finally {
      setIsUploadingPropertyImage(false);
      e.target.value = "";
    }
  };

  const fetchZestimate = async (silent = false) => {
    if (!cma || isFetchingZestimate) return;
    const address = [cma.address, cma.city, cma.state, cma.zip].filter(Boolean).join(", ");
    setIsFetchingZestimate(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-zestimate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ cmaId: cma.id, address }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch Zestimate");
      queryClient.invalidateQueries({ queryKey: ["cma", id] });
      if (!silent) toast({ title: "Zestimate updated", description: `$${data.zestimate.toLocaleString()}` });
    } catch (err: any) {
      if (!silent) toast({ title: "Zestimate unavailable", description: err.message, variant: "destructive" });
    } finally {
      setIsFetchingZestimate(false);
    }
  };

  const refreshPropertyData = async () => {
    if (!cma) return;
    setIsRefreshingProperty(true);
    try {
      const { data, error } = await supabase.functions.invoke("search-property-by-address", {
        body: {
          address: cma.address,
          city: cma.city,
          state: cma.state,
          zip: cma.zip,
        },
      });
      if (error) throw error;
      await updateCMA.mutateAsync({ property_data: data.property_data });
      queryClient.invalidateQueries({ queryKey: ["cma", id] });
      toast({ title: "Property data refreshed" });
    } catch (err: any) {
      toast({ title: "Refresh failed", description: err.message, variant: "destructive" });
    } finally {
      setIsRefreshingProperty(false);
    }
  };

  const handleSaveAgentPrice = async () => {
    if (!cma) return;
    setIsSavingAgentPrice(true);
    try {
      await updateCMA.mutateAsync({
        agent_price_low: agentPriceLow ? parseInt(agentPriceLow.replace(/\D/g, ""), 10) : null,
        agent_price_high: agentPriceHigh ? parseInt(agentPriceHigh.replace(/\D/g, ""), 10) : null,
      });
      toast({ title: "Agent price saved" });
    } catch {
      toast({ title: "Error", description: "Failed to save price", variant: "destructive" });
    } finally {
      setIsSavingAgentPrice(false);
    }
  };

  const handleSaveDesign = async () => {
    if (!cma) return;
    setIsSavingDesign(true);
    try {
      const { net_sheet_mortgage_payoff, net_sheet_commission_pct, net_sheet_closing_cost_pct, net_sheet_seller_credits, net_sheet_other_fees, ...rest } = designEdits;
      const updates = {
        ...rest,
        net_sheet_mortgage_payoff: net_sheet_mortgage_payoff ? parseInt(net_sheet_mortgage_payoff.replace(/\D/g, ""), 10) : null,
        net_sheet_commission_pct: net_sheet_commission_pct ? parseFloat(net_sheet_commission_pct) : null,
        net_sheet_closing_cost_pct: net_sheet_closing_cost_pct ? parseFloat(net_sheet_closing_cost_pct) : null,
        net_sheet_seller_credits: net_sheet_seller_credits ? parseInt(net_sheet_seller_credits.replace(/\D/g, ""), 10) : null,
        net_sheet_other_fees: net_sheet_other_fees ? parseInt(net_sheet_other_fees.replace(/\D/g, ""), 10) : null,
      };
      await updateCMA.mutateAsync(updates);
      toast({ title: "Design saved" });
    } catch {
      toast({ title: "Error", description: "Failed to save design", variant: "destructive" });
    } finally {
      setIsSavingDesign(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!cma) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-muted-foreground">CMA not found.</p>
        <Button variant="outline" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const fullAddress = [cma.address, cma.city, cma.state, cma.zip].filter(Boolean).join(", ");
  const publicUrl = `${window.location.origin}/cma/public/${cma.id}`;

  const pd = cma.property_data || {};
  const beds = pd.bedrooms;
  const baths = pd.bathrooms;
  const sqft = pd.livingArea || pd.squareFeet;
  const yearBuilt = pd.yearBuilt;
  const price = pd.price || pd.zestimate;
  const imageUrl =
    cma.custom_property_image_url ||
    designEdits.custom_property_image_url ||
    pd.desktopWebHdpImageLink ||
    pd.hiResImageLink ||
    pd.photos?.[0]?.mixedSources?.jpeg?.slice(-1)?.[0]?.url ||
    pd.photos?.[0]?.url ||
    pd.originalPhotos?.[0]?.mixedSources?.jpeg?.slice(-1)?.[0]?.url ||
    pd.originalPhotos?.[0]?.url ||
    pd.imgSrc ||
    null;

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-20 bg-card shadow-sm">
        <div className="flex items-center justify-between px-3 md:px-6 py-3 border-b border-border">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} title="Dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-semibold text-sm truncate">{fullAddress}</h1>
              <div className="flex items-center gap-2">
                {cma.seller_name && (
                  <span className="text-xs text-muted-foreground">{cma.seller_name}</span>
                )}
                <Badge variant="secondary" className={`text-xs ${
                  cma.converted_north_id ? "bg-blue-600 text-white" :
                  cma.is_published ? "bg-green-600 text-white" : "bg-gray-600 text-white"
                }`}>
                  {cma.converted_north_id ? "Converted" : cma.is_published ? "Published" : "Draft"}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePublishToggle}
              disabled={isPublishing}
              className="hidden md:flex gap-1.5"
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              {cma.is_published ? "Unpublish" : "Publish"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCopyLink}
              disabled={!cma.is_published}
              title="Copy public link"
            >
              {isCopied ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <LinkIcon className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleConvert}
              disabled={isConverting}
              className="gap-1.5 text-xs"
            >
              {isConverting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <ArrowRightLeft className="h-3 w-3" />
              )}
              {cma.converted_north_id ? "View North" : "Activate to North"}
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-[57px] z-10 bg-card border-b border-border">
        <div className="flex items-center gap-1 px-3 md:px-6 py-2 overflow-x-auto scrollbar-hide">
          {NAV_ITEMS.map((item) => (
            <Button
              key={item.id}
              variant="ghost"
              size="sm"
              className={cn(
                "flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md",
                activeSection === item.id ? "bg-secondary text-secondary-foreground" : "text-muted-foreground"
              )}
              onClick={() => handleSectionChange(item.id)}
              onMouseEnter={(e) => {
                if (activeSection !== item.id) {
                  e.currentTarget.style.backgroundColor = hoverColor;
                  e.currentTarget.style.color = getContrastColor(hoverColor);
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== item.id) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "";
                }
              }}
            >
              {item.icon}
              <span className="text-sm font-medium">{item.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 bg-muted/30">
      <div className="max-w-5xl mx-auto w-full px-4 py-6">
        {/* OVERVIEW */}
        {activeSection === "overview" && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left column: Subject Property + Agent Assigned */}
              <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Subject Property</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={refreshPropertyData}
                      disabled={isRefreshingProperty}
                      className="h-7 text-xs gap-1.5 text-muted-foreground"
                      title="Refresh property data &amp; image from Zillow"
                    >
                      {isRefreshingProperty ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="relative group">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={cma.address}
                        className="w-full h-40 object-cover rounded-md"
                      />
                    ) : (
                      <div className="w-full h-40 rounded-md bg-muted flex items-center justify-center">
                        <Upload className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    {/* Overlay upload button */}
                    <div className="absolute inset-0 flex items-center justify-center rounded-md opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePropertyImageUpload}
                        className="hidden"
                        id="cma-property-image-upload"
                      />
                      <Label htmlFor="cma-property-image-upload">
                        <Button variant="secondary" size="sm" asChild disabled={isUploadingPropertyImage}>
                          <span className="cursor-pointer gap-2 flex items-center">
                            {isUploadingPropertyImage ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                            {cma.custom_property_image_url ? "Replace Photo" : "Upload Photo"}
                          </span>
                        </Button>
                      </Label>
                    </div>
                  </div>
                  {cma.custom_property_image_url && (
                    <button
                      className="text-xs text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        setDesignEdits((prev) => ({ ...prev, custom_property_image_url: "" }));
                        updateCMA.mutate({ custom_property_image_url: null });
                      }}
                    >
                      Remove custom photo
                    </button>
                  )}
                  <div>
                    <p className="font-medium">{cma.address}</p>
                    <p className="text-sm text-muted-foreground">
                      {[cma.city, cma.state, cma.zip].filter(Boolean).join(", ")}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {beds && <div><span className="text-muted-foreground">Beds: </span><span className="font-medium">{beds}</span></div>}
                    {baths && <div><span className="text-muted-foreground">Baths: </span><span className="font-medium">{baths}</span></div>}
                    {sqft && <div><span className="text-muted-foreground">Sqft: </span><span className="font-medium">{sqft.toLocaleString()}</span></div>}
                    {yearBuilt && <div><span className="text-muted-foreground">Built: </span><span className="font-medium">{yearBuilt}</span></div>}
                  </div>
                  {cma.zillow_url && (
                    <a
                      href={cma.zillow_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline"
                    >
                      View on Zillow →
                    </a>
                  )}
                </CardContent>
              </Card>

              {/* Agent Assigned */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Agent Assigned</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSectionChange("design")}
                      className="h-7 text-xs gap-1.5 text-muted-foreground"
                    >
                      Edit
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {cma.custom_agent_name || cma.custom_agent_headshot_url ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="h-14 w-14 rounded-full border-2 border-border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                          {cma.custom_agent_headshot_url ? (
                            <img
                              src={cma.custom_agent_headshot_url}
                              alt={cma.custom_agent_name || "Agent"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{cma.custom_agent_name || "—"}</p>
                          {cma.custom_agent_email && (
                            <a href={`mailto:${cma.custom_agent_email}`} className="text-xs text-muted-foreground hover:text-foreground truncate block max-w-[140px]">
                              {cma.custom_agent_email}
                            </a>
                          )}
                        </div>
                      </div>
                      {cma.custom_agent_phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <a href={`tel:${cma.custom_agent_phone.replace(/\D/g, "")}`} className="text-muted-foreground hover:text-foreground">
                            {cma.custom_agent_phone}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
                      <User className="h-8 w-8 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">No agent assigned yet</p>
                      <Button variant="outline" size="sm" onClick={() => handleSectionChange("design")}>
                        Assign Agent
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              </div>{/* end left column */}

              {/* Right column: Pricing */}
              <CMAPricingDisplay cma={cma} comps={comps} />
            </div>

            {/* Seller Feedback */}
            {cma.seller_feedback_at && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Seller Pricing Feedback
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 flex-wrap">
                    {cma.seller_feedback_sentiment === "too_low" && (
                      <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-100 text-green-700">
                        <TrendingUp className="h-3.5 w-3.5" /> Price higher
                      </span>
                    )}
                    {cma.seller_feedback_sentiment === "looks_right" && (
                      <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-100 text-blue-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Looks right
                      </span>
                    )}
                    {cma.seller_feedback_sentiment === "too_high" && (
                      <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-700">
                        <TrendingDown className="h-3.5 w-3.5" /> Too high
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {new Date(cma.seller_feedback_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                  {cma.seller_feedback_comment && (
                    <p className="mt-3 text-sm text-muted-foreground italic">
                      "{cma.seller_feedback_comment}"
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Agent Recommended Price */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Your Recommended List Price
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Enter the price range you'll present to the seller. This appears on the public report alongside the AI suggestion.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[140px]">
                    <Label className="text-xs">Low</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        className="pl-6"
                        placeholder="570,000"
                        value={agentPriceLow}
                        onChange={(e) => setAgentPriceLow(e.target.value.replace(/[^\d]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
                      />
                    </div>
                  </div>
                  <span className="text-muted-foreground mt-5">–</span>
                  <div className="flex-1 min-w-[140px]">
                    <Label className="text-xs">High</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        className="pl-6"
                        placeholder="590,000"
                        value={agentPriceHigh}
                        onChange={(e) => setAgentPriceHigh(e.target.value.replace(/[^\d]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
                      />
                    </div>
                  </div>
                  <Button onClick={handleSaveAgentPrice} disabled={isSavingAgentPrice} className="mt-5 gap-2">
                    {isSavingAgentPrice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Zestimate Card */}
            <Card className="border-[#006AFF]/20 overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-4 py-3 bg-[#006AFF]/5 border-b border-[#006AFF]/10">
                  <div className="flex items-center gap-2">
                    <ZillowIcon className="h-5 w-5" />
                    <span className="text-sm font-medium text-[#006AFF]">Zestimate®</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchZestimate}
                    disabled={isFetchingZestimate}
                    className="h-7 text-xs text-[#006AFF] hover:text-[#006AFF] hover:bg-[#006AFF]/10 gap-1.5"
                  >
                    {isFetchingZestimate ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    Refresh
                  </Button>
                </div>
                <div className="px-4 py-3">
                  {cma.price ? (
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold">${cma.price.toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">estimated market value</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <p className="text-sm text-muted-foreground">No Zestimate fetched yet.</p>
                      <button
                        onClick={fetchZestimate}
                        disabled={isFetchingZestimate}
                        className="text-xs text-[#006AFF] hover:underline disabled:opacity-50"
                      >
                        Fetch now
                      </button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Map */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Comp Map</CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-hidden rounded-b-lg">
                <CMAMap
                  subjectAddress={fullAddress}
                  subjectLat={pd.latitude}
                  subjectLng={pd.longitude}
                  comps={comps.filter((c) => c.visible)}
                  primaryColor={cma.primary_brand_color || "#0c0f24"}
                />
              </CardContent>
            </Card>

            {/* Agent Notes */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Agent Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  placeholder="Add notes about this property, seller conversation, or CMA context..."
                  value={designEdits.agent_notes ?? cma.agent_notes ?? ""}
                  onChange={(e) =>
                    setDesignEdits((prev) => ({ ...prev, agent_notes: e.target.value }))
                  }
                  rows={4}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSaveDesign}
                  disabled={isSavingDesign}
                  className="gap-2"
                >
                  {isSavingDesign ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  Save Notes
                </Button>
              </CardContent>
            </Card>

          </div>
        )}

        {/* COMPS */}
        {activeSection === "comps" && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold mb-1">Comparable Properties</h2>
              <p className="text-sm text-muted-foreground">
                Manage comps shown in this CMA. Up to 5 can be visible on the report.
              </p>
            </div>
            <CMACompsManager
                cmaId={cma.id}
                zpid={cma.zpid}
                address={[cma.address, cma.city, cma.state, cma.zip].filter(Boolean).join(" ")}
              />
          </div>
        )}

        {/* ANALYSIS */}
        {activeSection === "analysis" && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold mb-1">AI Analysis</h2>
                <p className="text-sm text-muted-foreground">
                  AI-powered pricing recommendation and market analysis for your listing appointment.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 gap-2"
                onClick={handleRefreshMarketData}
                disabled={isRefreshingMarketData}
              >
                {isRefreshingMarketData ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Refresh Market Data
              </Button>
            </div>
            <CMAAnalysis cma={cma} />
            <CMAPricingDisplay cma={cma} comps={comps} />
          </div>
        )}

        {/* DESIGN */}
        {activeSection === "design" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-1">Customize CMA</h2>
              <p className="text-sm text-muted-foreground">
                Personalize the CMA with your branding, agent info, and seller details.
              </p>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Seller Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Seller Name</Label>
                  <Input
                    value={designEdits.seller_name ?? ""}
                    onChange={(e) => setDesignEdits((prev) => ({ ...prev, seller_name: e.target.value }))}
                    placeholder="e.g. John & Jane Smith"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Net Sheet Inputs */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Net Sheet Inputs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  These are locked values. Sellers can move a price slider on the public report to see how proceeds change.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Mortgage Payoff ($)</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        className="pl-6 text-sm"
                        placeholder="200,000"
                        value={designEdits.net_sheet_mortgage_payoff ?? ""}
                        onChange={(e) => setDesignEdits((prev) => ({ ...prev, net_sheet_mortgage_payoff: e.target.value.replace(/[^\d]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",") }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Commission %</Label>
                    <Input
                      className="mt-1 text-sm"
                      placeholder="6.0"
                      value={designEdits.net_sheet_commission_pct ?? ""}
                      onChange={(e) => setDesignEdits((prev) => ({ ...prev, net_sheet_commission_pct: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Closing Costs %</Label>
                    <Input
                      className="mt-1 text-sm"
                      placeholder="1.0"
                      value={designEdits.net_sheet_closing_cost_pct ?? ""}
                      onChange={(e) => setDesignEdits((prev) => ({ ...prev, net_sheet_closing_cost_pct: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Seller Credits ($)</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        className="pl-6 text-sm"
                        placeholder="0"
                        value={designEdits.net_sheet_seller_credits ?? ""}
                        onChange={(e) => setDesignEdits((prev) => ({ ...prev, net_sheet_seller_credits: e.target.value.replace(/[^\d]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",") }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Other Fees ($)</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        className="pl-6 text-sm"
                        placeholder="0"
                        value={designEdits.net_sheet_other_fees ?? ""}
                        onChange={(e) => setDesignEdits((prev) => ({ ...prev, net_sheet_other_fees: e.target.value.replace(/[^\d]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",") }))}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Agent Branding</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Agent selector */}
                {agents.length > 0 && (
                  <div>
                    <Label>Select from Roster</Label>
                    <Select
                      value={selectedAgentId || "none"}
                      onValueChange={(v) => setSelectedAgentId(v === "none" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select an agent…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Enter manually —</SelectItem>
                        {agents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={agent.headshot_url || undefined} />
                                <AvatarFallback className="text-[10px]">
                                  {agent.name[0]?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              {agent.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Name */}
                <div>
                  <Label>Agent Name</Label>
                  <Input
                    value={designEdits.custom_agent_name ?? ""}
                    onChange={(e) => setDesignEdits((prev) => ({ ...prev, custom_agent_name: e.target.value }))}
                    placeholder="Your name"
                    className="mt-1"
                  />
                </div>

                {/* Headshot */}
                <div>
                  <Label>Agent Headshot</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="h-16 w-16 rounded-full border-2 border-border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {designEdits.custom_agent_headshot_url ? (
                        <img
                          src={designEdits.custom_agent_headshot_url}
                          alt="Headshot"
                          className="w-full h-full object-cover"
                          onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                        />
                      ) : (
                        <User className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      {selectedAgentId && selectedAgentId !== "none" && agents.find(a => a.id === selectedAgentId)?.headshot_url ? (
                        <p className="text-xs text-muted-foreground">Photo from roster</p>
                      ) : null}
                      <input type="file" accept="image/*" onChange={handleHeadshotUpload} className="hidden" id="cma-headshot-upload" />
                      <Label htmlFor="cma-headshot-upload">
                        <Button variant="outline" size="sm" asChild disabled={isUploadingHeadshot}>
                          <span className="cursor-pointer gap-2 flex items-center">
                            {isUploadingHeadshot ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                            {designEdits.custom_agent_headshot_url ? "Replace" : "Upload Photo"}
                          </span>
                        </Button>
                      </Label>
                      {designEdits.custom_agent_headshot_url && (
                        <Button variant="ghost" size="sm" className="text-muted-foreground gap-2 justify-start"
                          onClick={() => setDesignEdits((prev) => ({ ...prev, custom_agent_headshot_url: "" }))}>
                          <X className="h-3 w-3" /> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Phone</Label>
                  <Input
                    value={designEdits.custom_agent_phone ?? ""}
                    onChange={(e) => setDesignEdits((prev) => ({ ...prev, custom_agent_phone: formatPhone(e.target.value) }))}
                    placeholder="(555) 123-4567"
                    type="tel"
                    className="mt-1"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</Label>
                  <Input
                    value={designEdits.custom_agent_email ?? ""}
                    onChange={(e) => setDesignEdits((prev) => ({ ...prev, custom_agent_email: e.target.value }))}
                    placeholder="agent@email.com"
                    type="email"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Header Logo</Label>
                  <p className="text-xs text-muted-foreground mb-1">Shown in the top-left of the hero</p>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="w-40 h-14 rounded-lg border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {designEdits.custom_logo_url ? (
                        <img
                          src={designEdits.custom_logo_url}
                          alt="Logo preview"
                          className="max-w-full max-h-full object-contain p-1"
                          onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                        />
                      ) : (
                        <Upload className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="cma-logo-upload"
                      />
                      <Label htmlFor="cma-logo-upload">
                        <Button variant="outline" size="sm" asChild disabled={isUploadingLogo}>
                          <span className="cursor-pointer gap-2 flex items-center">
                            {isUploadingLogo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                            Upload Logo
                          </span>
                        </Button>
                      </Label>
                      {designEdits.custom_logo_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground gap-2 justify-start"
                          onClick={() => setDesignEdits((prev) => ({ ...prev, custom_logo_url: "" }))}
                        >
                          <X className="h-3 w-3" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Footer Logo</Label>
                  <p className="text-xs text-muted-foreground mb-1">Shown in the "Ready to list" CTA section alongside the agent headshot</p>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="w-40 h-14 rounded-lg border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {designEdits.custom_footer_logo_url ? (
                        <img
                          src={designEdits.custom_footer_logo_url}
                          alt="Footer logo preview"
                          className="max-w-full max-h-full object-contain p-1"
                          onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                        />
                      ) : (
                        <Upload className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFooterLogoUpload}
                        className="hidden"
                        id="cma-footer-logo-upload"
                      />
                      <Label htmlFor="cma-footer-logo-upload">
                        <Button variant="outline" size="sm" asChild disabled={isUploadingFooterLogo}>
                          <span className="cursor-pointer gap-2 flex items-center">
                            {isUploadingFooterLogo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                            Upload Logo
                          </span>
                        </Button>
                      </Label>
                      {designEdits.custom_footer_logo_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground gap-2 justify-start"
                          onClick={() => setDesignEdits((prev) => ({ ...prev, custom_footer_logo_url: "" }))}
                        >
                          <X className="h-3 w-3" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Brand Colors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label>Primary Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={designEdits.primary_brand_color || "#000000"}
                        onChange={(e) => setDesignEdits((prev) => ({ ...prev, primary_brand_color: e.target.value }))}
                        className="h-9 w-12 rounded border cursor-pointer"
                      />
                      <Input
                        value={designEdits.primary_brand_color || "#000000"}
                        onChange={(e) => setDesignEdits((prev) => ({ ...prev, primary_brand_color: e.target.value }))}
                        placeholder="#000000"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <Label>Secondary Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={designEdits.secondary_brand_color || "#666666"}
                        onChange={(e) => setDesignEdits((prev) => ({ ...prev, secondary_brand_color: e.target.value }))}
                        className="h-9 w-12 rounded border cursor-pointer"
                      />
                      <Input
                        value={designEdits.secondary_brand_color || "#666666"}
                        onChange={(e) => setDesignEdits((prev) => ({ ...prev, secondary_brand_color: e.target.value }))}
                        placeholder="#666666"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleSaveDesign}
              disabled={isSavingDesign}
              className="gap-2"
            >
              {isSavingDesign ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Design
            </Button>
          </div>
        )}

        {/* SHARE */}
        {activeSection === "share" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-1">Share CMA</h2>
              <p className="text-sm text-muted-foreground">
                Share this CMA with sellers via public link or email.
              </p>
            </div>

            {/* Publish Toggle */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Public Link</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {cma.is_published ? "CMA is Published" : "CMA is Draft"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {cma.is_published
                        ? "Anyone with the link can view this CMA."
                        : "Publish to share with sellers."}
                    </p>
                  </div>
                  <Button
                    variant={cma.is_published ? "outline" : "default"}
                    size="sm"
                    onClick={handlePublishToggle}
                    disabled={isPublishing}
                    className="gap-2"
                  >
                    {isPublishing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : cma.is_published ? (
                      <Globe className="h-4 w-4 text-green-600" />
                    ) : (
                      <Globe className="h-4 w-4" />
                    )}
                    {cma.is_published ? "Unpublish" : "Publish"}
                  </Button>
                </div>

                {cma.is_published && (
                  <div className="flex items-center gap-2 mt-2">
                    <Input value={publicUrl} readOnly className="text-sm bg-muted" />
                    <Button variant="outline" size="sm" onClick={handleCopyLink} className="gap-2 flex-shrink-0">
                      {isCopied ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <LinkIcon className="h-4 w-4" />
                      )}
                      {isCopied ? "Copied!" : "Copy"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload files or add links (Canva, Google Drive, etc.) for sellers to access.
                </p>
                <CMADocuments cmaId={cma.id} teamId={cma.team_id!} readOnly={false} />
              </CardContent>
            </Card>

            {/* Email Section */}
            <CMAEmailShare
                cmaId={cma.id}
                isPublished={cma.is_published}
                publicUrl={publicUrl}
                address={fullAddress}
                agentName={cma.custom_agent_name}
                agentPhone={cma.custom_agent_phone}
                agentEmail={cma.custom_agent_email}
                agentHeadshotUrl={cma.custom_agent_headshot_url}
                logoUrl={cma.custom_logo_url}
                primaryColor={cma.primary_brand_color || "#0c0f24"}
                sellerName={cma.seller_name}
                priceLow={cma.suggested_price_low}
                priceHigh={cma.suggested_price_high}
                propertyImageUrl={cma.custom_property_image_url || cma.property_data?.imgSrc || null}
              />

            {/* Convert to North */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" />
                  Activate as North Report
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Won the listing? Convert this CMA into an active North Report to start tracking performance, showings, and seller updates.
                </p>
                {cma.converted_north_id ? (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/seller-report/${cma.converted_north_id}`)}
                    className="gap-2"
                  >
                    <ArrowRightLeft className="h-4 w-4" />
                    View North Report
                  </Button>
                ) : (
                  <Button
                    onClick={handleConvert}
                    disabled={isConverting}
                    variant="gold"
                    className="gap-2"
                  >
                    {isConverting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRightLeft className="h-4 w-4" />
                    )}
                    Activate as North Report
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// Inline email share component
function CMAEmailShare({
  cmaId,
  isPublished,
  publicUrl,
  address,
  agentName,
  agentPhone,
  agentEmail,
  agentHeadshotUrl,
  logoUrl,
  primaryColor,
  sellerName,
  priceLow,
  priceHigh,
  propertyImageUrl,
}: {
  cmaId: string;
  isPublished: boolean;
  publicUrl: string;
  address: string;
  agentName: string | null;
  agentPhone: string | null;
  agentEmail: string | null;
  agentHeadshotUrl: string | null;
  logoUrl: string | null;
  primaryColor: string;
  sellerName: string | null;
  priceLow: number | null;
  priceHigh: number | null;
  propertyImageUrl: string | null;
}) {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const brandColor = primaryColor || "#0c0f24";
  const northLogoUrl = "https://zkqcqgjbsigjdwzmnwmp.supabase.co/storage/v1/object/public/logos/north-logo.png";
  const buildHtmlBody = (greeting: string) => `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;padding:32px 16px;">
  <tr><td align="center">
    <table width="100%" cellpadding="0" cellspacing="0" style="max-width:580px;">

      <!-- Logo row -->
      ${logoUrl ? `
      <tr><td style="background:#ffffff;border-radius:12px 12px 0 0;padding:24px 32px;text-align:center;">
        <img src="${logoUrl}" alt="" style="max-height:40px;max-width:160px;object-fit:contain;display:inline-block;">
      </td></tr>` : ""}

      ${propertyImageUrl ? `
      <!-- Property hero image -->
      <tr><td style="padding:0;line-height:0;">
        <img src="${propertyImageUrl}" alt="${address}"
             style="width:100%;max-height:280px;object-fit:cover;display:block;${!logoUrl ? "border-radius:12px 12px 0 0;" : ""}">
      </td></tr>` : ""}

      <!-- Body card -->
      <tr><td style="background:#ffffff;padding:40px 40px 32px;${!propertyImageUrl && !logoUrl ? "border-radius:12px 12px 0 0;" : ""}">

        <!-- Heading -->
        <h1 style="margin:0 0 8px;font-size:24px;font-weight:700;color:#0f172a;line-height:1.3;">
          Your Comparative Market Analysis is Ready
        </h1>
        <p style="margin:0 0 24px;font-size:15px;color:#64748b;">
          ${address}
        </p>

        <!-- Greeting -->
        <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
          Hi${greeting ? ` ${greeting}` : ""},
        </p>
        <p style="margin:0 0 16px;font-size:15px;color:#334155;line-height:1.6;">
          I've put together a detailed Comparative Market Analysis for your property. Inside you'll find a full pricing recommendation based on current market conditions, recent comparable sales, and active listings in your area.
        </p>

        <!-- CTA button -->
        <table width="100%" cellpadding="0" cellspacing="0" style="margin:28px 0 24px;">
          <tr><td align="center">
            <a href="${publicUrl}"
               style="display:inline-block;background:${brandColor};color:#ffffff;font-size:16px;font-weight:600;
                      text-decoration:none;padding:14px 36px;border-radius:8px;letter-spacing:0.01em;">
              View My CMA Report →
            </a>
          </td></tr>
        </table>

        <p style="margin:0 0 32px;font-size:13px;color:#94a3b8;text-align:center;">
          Or copy this link:<br>
          <a href="${publicUrl}" style="color:${brandColor};word-break:break-all;">${publicUrl}</a>
        </p>

        <!-- Divider -->
        <hr style="border:none;border-top:1px solid #e2e8f0;margin:0 0 24px;">

        <!-- Agent signature -->
        <table cellpadding="0" cellspacing="0">
          <tr>
            ${agentHeadshotUrl ? `
            <td style="padding-right:16px;vertical-align:top;">
              <img src="${agentHeadshotUrl}" alt="${agentName || ""}"
                   style="width:56px;height:56px;border-radius:50%;object-fit:cover;display:block;">
            </td>` : ""}
            <td style="vertical-align:middle;">
              <p style="margin:0 0 2px;font-size:15px;font-weight:600;color:#0f172a;">${agentName || "Your Agent"}</p>
              ${agentPhone ? `<p style="margin:0 0 2px;font-size:13px;color:#64748b;">${agentPhone}</p>` : ""}
              ${agentEmail ? `<p style="margin:0;font-size:13px;color:#64748b;">${agentEmail}</p>` : ""}
            </td>
          </tr>
        </table>

      </td></tr>

      <!-- Footer -->
      <tr><td style="background:#f8fafc;border-top:1px solid #e2e8f0;border-radius:0 0 12px 12px;padding:16px 32px;text-align:center;">
        <p style="margin:0 0 6px;font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.08em;">Powered by</p>
        <img src="${northLogoUrl}" alt="North" style="max-height:24px;max-width:80px;object-fit:contain;display:inline-block;opacity:0.6;">
        <p style="margin:8px 0 0;font-size:11px;color:#94a3b8;">You received this because your agent shared a CMA with you.</p>
      </td></tr>

    </table>
  </td></tr>
</table>
</body>
</html>`;

  const handleSend = async () => {
    if (!email.trim()) {
      toast({ title: "Email is required", variant: "destructive" });
      return;
    }
    if (!isPublished) {
      toast({ title: "Publish first", description: "Publish the CMA before sending via email.", variant: "destructive" });
      return;
    }

    setIsSending(true);
    try {
      const greeting = name || sellerName || "";
      const htmlBody = buildHtmlBody(greeting);

      const { error } = await supabase.functions.invoke("send-cma-email", {
        body: {
          cmaId,
          recipientEmail: email.trim(),
          recipientName: name.trim() || undefined,
          subject: `Your CMA for ${address}`,
          htmlBody,
        },
      });

      if (error) throw error;

      toast({ title: "Email sent!", description: `CMA sent to ${email}` });
      setEmail("");
      setName("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send email";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  const previewHtml = buildHtmlBody(sellerName || "");

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Email to Seller</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-xs"
            onClick={() => setShowPreview((v) => !v)}
          >
            <Eye className="h-3.5 w-3.5" />
            {showPreview ? "Hide Preview" : "Preview Email"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {showPreview && (
          <div className="rounded-lg border border-border overflow-hidden bg-muted">
            <p className="text-xs text-muted-foreground px-3 py-2 border-b border-border bg-background">
              Email Preview
            </p>
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="w-full border-0"
              style={{ height: "520px" }}
              sandbox="allow-same-origin"
            />
          </div>
        )}
        {!isPublished && (
          <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
            Publish the CMA first to send via email.
          </p>
        )}
        <div>
          <Label>Recipient Name (optional)</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Smith"
          />
        </div>
        <div>
          <Label>Recipient Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seller@example.com"
          />
        </div>
        <Button onClick={handleSend} disabled={isSending || !isPublished} className="gap-2">
          {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
          {isSending ? "Sending..." : "Send Email"}
        </Button>
      </CardContent>
    </Card>
  );
}
